"use client";

import {
  useAccountActionPermissions,
  useCurrentUser,
  usePausedZaps,
  useSplitTreatments,
  useUserAccountRole,
} from "@zapier/client";
import {
  OwnerFilterController,
  AppFilterController,
  useEmitZapSearchInteractionEvent,
  useRouterParams,
  ZapSearchInteractionOptions,
  type ExclusiveMode,
  type FilterDefinition,
  type LegacyMigrationResult,
  type QuickFilterContext,
  type QuickFilterDefinition,
  type StateChipDefinition,
} from "@zapier/shared";
import { PENDING_FOLDER_NAME } from "@zapier/utils";
import { StatusFilterController } from "./StatusFilterController";
import { LastRunFilterController } from "./LastRunFilterController";
import { ConnectionFilterController } from "./ConnectionFilterController";

export interface ZapFilterConfig {
  filters: FilterDefinition[];
  quickFilters: QuickFilterDefinition[];
  quickFilterContext: QuickFilterContext;
  stateChips: StateChipDefinition[];
  exclusiveMode?: ExclusiveMode;
  onFilterInteraction: (type: string) => void;
  legacyMigrate: (
    parameters: ReturnType<typeof useRouterParams>,
    user: { id: number } | undefined,
  ) => LegacyMigrationResult;
  hasActiveFilters: boolean;
  search: string | undefined;
}

/**
 * Assembles the Zaps filter-bar config for the generic `<FilterBar>`.
 * Ports the quick-filter mutations, gating, and legacy migration that
 * previously lived inline in `ZapFilterBar` + `useFilterBarState` +
 * `useLegacyFilterMigration`.
 */
export function useZapFilterConfig(): ZapFilterConfig {
  const { user } = useCurrentUser();
  const { isSuperAdmin, isAccountOwner } = useUserAccountRole();
  const { isSharingEnabled } = useAccountActionPermissions();
  const { isLastRunEnabled } = useSplitTreatments();
  // The old code lazily enabled this only when a filter surface was open. The
  // config has no focus signal, so enable it unconditionally (eager count).
  const { meta: pausedZapsMeta } = usePausedZaps({ enabled: true });
  const {
    shared,
    virtual,
    status,
    lastRun,
    owners,
    apps,
    connections,
    search,
  } = useRouterParams();
  const { emit } = useEmitZapSearchInteractionEvent();

  const filters: FilterDefinition[] = [
    {
      type: "status",
      icon: "statusStatus",
      labelKey: "status.label",
      paramNames: ["status"],
      Controller: StatusFilterController,
    },
    {
      type: "owner",
      icon: "personCircle",
      labelKey: "owner.label",
      paramNames: ["owners", "ownersOperator"],
      Controller: OwnerFilterController,
    },
    {
      type: "app",
      icon: "navApps",
      labelKey: "app.label",
      paramNames: ["apps", "appsOperator"],
      Controller: AppFilterController,
    },
    {
      type: "connection",
      icon: "actionLink",
      labelKey: "connection.label",
      paramNames: ["connections", "connectionsOperator"],
      Controller: ConnectionFilterController,
    },
    ...(isLastRunEnabled
      ? [
          {
            type: "lastRun",
            icon: "miscClock",
            labelKey: "lastRun.label",
            paramNames: ["lastRun"],
            Controller: LastRunFilterController,
          } satisfies FilterDefinition,
        ]
      : []),
  ];

  const quickFilters: QuickFilterDefinition[] = [
    {
      key: "ownedByMe",
      icon: "personOne",
      labelKey: "quickFilters.ownedByMe",
      isDisabled: (context) => !context.isUserResolved,
      apply: (setParams) => {
        if (!user) return;
        emit({ optionSelected: ZapSearchInteractionOptions.OwnedByMe });
        setParams({ owners: String(user.id), page: 1 }, [
          "owner",
          "ownersOperator",
          "shared",
          "virtual",
        ]);
      },
    },
    {
      key: "sharedWithMe",
      icon: "personGroup",
      labelKey: "quickFilters.sharedWithMe",
      trailingIconKey: "actionLock",
      tooltipKey: "quickFilters.sharedWithMeDisabledTooltip",
      isDisabled: (context) =>
        !context.isUserResolved || !context.isSharingEnabled,
      apply: (setParams) => {
        if (!user) return;
        emit({ optionSelected: ZapSearchInteractionOptions.SharedWithMe });
        setParams(
          { owners: String(user.id), ownersOperator: "none", page: 1 },
          ["owner", "shared", "virtual"],
        );
      },
    },
    {
      key: "deactivated",
      icon: "personCircle",
      labelKey: "quickFilters.deactivated",
      isAvailable: (context) => context.showDeactivated,
      apply: (setParams) => {
        emit({ optionSelected: ZapSearchInteractionOptions.DeactivatedUsers });
        setParams({ shared: "deactivated", page: 1 }, [
          "owner",
          "owners",
          "ownersOperator",
          "virtual",
        ]);
      },
    },
    {
      key: "billingPending",
      icon: "miscHand",
      labelKey: "quickFilters.billingPending",
      isAvailable: (context) => context.showBillingPending,
      apply: (setParams) => {
        emit({ optionSelected: ZapSearchInteractionOptions.Pending });
        setParams({ virtual: PENDING_FOLDER_NAME, page: 1 }, [
          "app",
          "apps",
          "appsOperator",
          "connection",
          "connections",
          "connectionsOperator",
          "lastRun",
          "owner",
          "owners",
          "ownersOperator",
          "search",
          "shared",
          "status",
        ]);
      },
    },
  ];

  const isDeactivated = shared === "deactivated";
  const isBillingPending = virtual === PENDING_FOLDER_NAME;
  const hasActiveFilters =
    !!status ||
    !!lastRun ||
    !!owners ||
    !!apps ||
    !!connections ||
    isDeactivated ||
    isBillingPending;
  const canViewDeactivatedUsers = isSuperAdmin || isAccountOwner;
  const showDeactivated =
    canViewDeactivatedUsers && !isDeactivated && !isBillingPending;
  const hasPendingZaps = (pausedZapsMeta?.count ?? 0) > 0;
  const showBillingPending =
    hasPendingZaps && !isBillingPending && !isDeactivated;

  const quickFilterContext: QuickFilterContext = {
    isUserResolved: !!user,
    isSharingEnabled,
    showDeactivated,
    showBillingPending,
  };

  // Billing-pending wins the URL tie-break against deactivated (mirrors
  // useZapsList prioritizing virtual===PENDING_FOLDER_NAME), so it's listed
  // first — FilterBar renders only the first active state chip.
  const stateChips: StateChipDefinition[] = [
    {
      key: "billingPending",
      icon: "miscHand",
      labelKey: "quickFilters.billingPending",
      isActive: isBillingPending,
      omitParams: ["virtual"],
    },
    {
      key: "deactivated",
      icon: "personCircle",
      labelKey: "quickFilters.deactivated",
      isActive: isDeactivated,
      omitParams: ["shared"],
    },
  ];

  const exclusiveMode: ExclusiveMode | undefined = isBillingPending
    ? {
        inputDisabled: true,
        placeholderKey: "searchPlaceholderPendingBilling",
        hideFilters: true,
      }
    : undefined;

  const onFilterInteraction = (type: string) => {
    const option = {
      owner: ZapSearchInteractionOptions.Owner,
      app: ZapSearchInteractionOptions.AppUsed,
      connection: ZapSearchInteractionOptions.AppConnection,
      status: ZapSearchInteractionOptions.ZapStatus,
      lastRun: ZapSearchInteractionOptions.LastRun,
    }[type];
    if (option) emit({ optionSelected: option });
  };

  const legacyMigrate = (
    parameters: ReturnType<typeof useRouterParams>,
    migrateUser: { id: number } | undefined,
  ): LegacyMigrationResult => {
    const {
      owner,
      app,
      connection,
      shared: parameterShared,
      owners,
      apps,
      connections,
    } = parameters;

    const isOwnedByMe = parameterShared === "me";
    const isSharedWithMe = parameterShared === "-me";
    const hasLegacyParameters =
      !!owner || !!app || !!connection || isOwnedByMe || isSharedWithMe;

    const set: Record<string, string> = {};
    const omit: string[] = [];

    // `owners` can come from legacy `owner`, `shared=me`, or `shared=-me`.
    // Precedence: an explicit owner id beats the sharing toggle.
    let ownersTarget: string | undefined;
    let ownersOperatorTarget: string | undefined;
    if (owner) {
      ownersTarget = owner;
    } else if ((isOwnedByMe || isSharedWithMe) && migrateUser) {
      ownersTarget = String(migrateUser.id);
      if (isSharedWithMe) ownersOperatorTarget = "none";
    }
    if (ownersTarget && !owners) {
      set.owners = ownersTarget;
      if (ownersOperatorTarget) set.ownersOperator = ownersOperatorTarget;
    }
    if (owner) omit.push("owner");
    if (isOwnedByMe || isSharedWithMe) omit.push("shared");

    if (app) {
      // Legacy `app` is `<id>::<name>`; the new `apps` carries bare ids.
      if (!apps) set.apps = app.split("::")[0];
      omit.push("app");
    }
    if (connection) {
      if (!connections) set.connections = connection;
      omit.push("connection");
    }

    // Only the sharing toggle needs the current user id; defer until it loads.
    const defer = (isOwnedByMe || isSharedWithMe) && !migrateUser;

    return { set, omit, defer, hasLegacyParameters };
  };

  return {
    filters,
    quickFilters,
    quickFilterContext,
    stateChips,
    exclusiveMode,
    onFilterInteraction,
    legacyMigrate,
    hasActiveFilters,
    search: search ?? undefined,
  };
}
